import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { INSTANCES_PAGE_SIZE, SUMMARY_FETCH_LIMIT, alertInstancesActions } from './slice';

/**
 * Alert Instances saga (P3 — Operations). Reads the incident feed + applies triage actions against
 * the v5 /alerts/instances API (org-wide-admin, server-enforced). On triage failure we toast + re-pull.
 */
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const selectCurrentStatus = (orgID) => (state) => state.AlertInstances.byOrg[orgID]?.status;
const okStatus = (res) => res && (res.statusCode === 200 || res.statusCode === 201);

function* fetchInstances(action) {
	const orgID = yield select(selectOrgID);
	// A triage-error retry (below) dispatches loadInstances with no status — reuse whichever tab
	// was already selected instead of silently refetching 'active' into that tab's slot.
	const status = action.payload?.status || (yield select(selectCurrentStatus(orgID))) || 'active';
	try {
		// First keyset page — the cursor for "Load more" comes back in res.pagination.nextCursor.
		const res = yield call(() => SuperFetch.get('/alerts/instances', {}, { status, limit: INSTANCES_PAGE_SIZE }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load operations.');
		yield put(
			alertInstancesActions.loadInstancesSuccess({
				orgID,
				instances: res.data || [],
				nextCursor: res.pagination?.nextCursor ?? null
			})
		);
	} catch (error) {
		yield put(alertInstancesActions.loadInstancesError({ orgID, error: error.message || String(error) }));
	}
}

// "Load more" — fetch the next keyset page for the current status tab (beforeID = stored nextCursor)
// and append it. No-op if there is no cursor (guarded here and in the button's `exhausted` gate).
function* fetchMoreInstances() {
	const orgID = yield select(selectOrgID);
	const bucket = yield select((state) => state.AlertInstances.byOrg[orgID]);
	const status = bucket?.status || 'active';
	const beforeID = bucket?.nextCursor;
	if (!beforeID) return;
	try {
		const res = yield call(() =>
			SuperFetch.get('/alerts/instances', {}, { status, limit: INSTANCES_PAGE_SIZE, beforeID })
		);
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load more operations.');
		yield put(
			// `status` lets the reducer drop this page if the user switched tabs while it was in flight.
			alertInstancesActions.loadMoreInstancesSuccess({
				orgID,
				status,
				instances: res.data || [],
				nextCursor: res.pagination?.nextCursor ?? null
			})
		);
	} catch (error) {
		yield put(alertInstancesActions.loadMoreInstancesError({ orgID, error: error.message || String(error) }));
	}
}

// AO2-2 SummaryStrip — always the org's active incidents (up to SUMMARY_FETCH_LIMIT), independent
// of whichever status tab is selected. @todo a real backend count endpoint once orgs can exceed
// the cap; selectSummaryAtCap flags the UI when this fetch hit it so counts read as a floor.
function* fetchSummary() {
	const orgID = yield select(selectOrgID);
	try {
		const res = yield call(() =>
			SuperFetch.get('/alerts/instances', {}, { status: 'active', limit: SUMMARY_FETCH_LIMIT })
		);
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load the alert summary.');
		yield put(alertInstancesActions.loadSummarySuccess({ orgID, instances: res.data || [] }));
	} catch (error) {
		yield put(alertInstancesActions.loadSummaryError({ orgID, error: error.message || String(error) }));
	}
}

// Resolved-count bucket (mirrors fetchSummary but for the terminal 'resolved' status) — feeds the
// Resolved status-tab badge, which the active summary can't cover. Capped at SUMMARY_FETCH_LIMIT.
function* fetchResolvedSummary() {
	const orgID = yield select(selectOrgID);
	try {
		const res = yield call(() =>
			SuperFetch.get('/alerts/instances', {}, { status: 'resolved', limit: SUMMARY_FETCH_LIMIT })
		);
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load the resolved summary.');
		yield put(alertInstancesActions.loadResolvedSummarySuccess({ orgID, instances: res.data || [] }));
	} catch (error) {
		yield put(alertInstancesActions.loadResolvedSummaryError({ orgID, error: error.message || String(error) }));
	}
}

function* triage(action) {
	const orgID = yield select(selectOrgID);
	const { alertInstanceID, action: act, body } = action.payload;
	try {
		const res = yield call(() => SuperFetch.post(`/alerts/instances/${alertInstanceID}/${act}`, body || {}));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to update the incident.');
		yield put(alertInstancesActions.triageInstanceSuccess({ orgID, instance: res.data }));
	} catch (error) {
		Notification('error', 'Could not update the incident.');
		yield put(alertInstancesActions.triageInstanceError({ orgID }));
		yield put(alertInstancesActions.loadInstances({ orgID }));
	}
}

export default function* alertInstancesSaga() {
	yield all([
		takeLatest(alertInstancesActions.loadInstances, fetchInstances),
		takeLatest(alertInstancesActions.loadMoreInstances, fetchMoreInstances),
		takeLatest(alertInstancesActions.loadSummary, fetchSummary),
		takeLatest(alertInstancesActions.loadResolvedSummary, fetchResolvedSummary),
		takeEvery(alertInstancesActions.triageInstance, triage)
	]);
}
